import React, { useEffect, useState } from 'react';
import { Flex, FlexProps, Text } from '@chakra-ui/react';

import arrowIcon from '../../Resources/Images/Icons/arrow_icon.svg';
import { IssuerType } from '../../Utils/backStageApi';
import { numAddComma } from '../../Utils/common';
import { GenericTable } from '../Common/GenericTable';
import { TablePager } from '../Common/TablePager';

export interface IssuerStatItem extends IssuerType {}

export type IssuerStatSortField = 'issued_count' | 'verified_count' | null;

interface Props extends Pick<FlexProps, 'mt'> {
  issuers: IssuerStatItem[];
  sortField: IssuerStatSortField;
  sortDirection: 'asc' | 'desc';
  onSortToggle: (field: Exclude<IssuerStatSortField, null>) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20] as const;

const TABLE_CELL_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#00292B',
};

export const IssuerStatTable: React.FC<Props> = ({ issuers, sortField, sortDirection, onSortToggle, mt }) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const totalItems = issuers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const paginated = issuers.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [issuers]);

  const columns = [
    { label: '名稱', width: '320px', paddingLeft: '40px' },
    { label: '證書名稱', width: '320px' },
    {
      label: '發行數',
      width: '200px',
      sortDirection: sortField === 'issued_count' ? sortDirection : undefined,
      onSort: () => onSortToggle('issued_count'),
      sortArrowSrc: sortField === 'issued_count' ? arrowIcon : undefined,
      sortArrowAlt: '排序',
    },
    {
      label: '驗證數',
      width: '200px',
      sortDirection: sortField === 'verified_count' ? sortDirection : undefined,
      onSort: () => onSortToggle('verified_count'),
      sortArrowSrc: sortField === 'verified_count' ? arrowIcon : undefined,
      sortArrowAlt: '排序',
    },
  ];

  const renderRow = (item: IssuerStatItem) => (
    <Flex key={item.id} w="1040px" minH="60px" align="stretch">
      <Flex w="320px" minW="320px" align="center" pl="40px" pr="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.name}>
          {item.name}
        </Text>
      </Flex>

      <Flex w="320px" minW="320px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.cert_name}>
          {item.cert_name}
        </Text>
      </Flex>

      <Flex w="200px" minW="200px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>
          {numAddComma(item.issued_count)}/{item.annual_limit === null ? '無上限' : numAddComma(item.annual_limit)}
        </Text>
      </Flex>

      <Flex w="200px" minW="200px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{numAddComma(item.verified_count)}</Text>
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" w="1040px" mt={mt}>
      <GenericTable columns={columns} isEmpty={paginated.length === 0} emptyText="目前沒有機構資料">
        {paginated.map(renderRow)}
      </GenericTable>

      <TablePager
        mt="50px"
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={safePage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        labels={{
          show: '顯示',
          itemsUnit: '筆',
          range: (s, e) => `${s} - ${e} 筆`,
          prevPage: '上一頁',
          nextPage: '下一頁',
        }}
      />
    </Flex>
  );
};

export default IssuerStatTable;
